import { useState, useCallback, useRef } from 'react';
import { Message, StreamData, StreamState, StreamContext, UsageMetadata } from '../../../types/chat';
import { getAiApiUrl } from '../../../config';
import { notify } from '../../../utils/toast';
import { updateTokenUsage } from '../../../services/api/tokenApi';

const MAX_BUFFER_SIZE = 1024 * 1024; // 1MB max buffer size

export const useStreamState = () => {
  const [state, setState] = useState<StreamState>({
    currentTaskId: null,
    sending: false,
    abortController: null
  });

  const bufferRef = useRef<string>('');
  const decoderRef = useRef(new TextDecoder());

  const resetState = useCallback(() => {
    setState({
      currentTaskId: null,
      sending: false,
      abortController: null
    });
    bufferRef.current = '';
  }, []);

  const stopStream = useCallback(async (
    auth: { jwt: string; did: string, walletAddress: string, nonce: string },
    onStop?: (message: Message) => void
  ) => {
    if (state.currentTaskId && state.abortController) {
      try {
        state.abortController.abort();

        await fetch(`${getAiApiUrl()}/assistant/${state.currentTaskId}/stop`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${auth.jwt}`
          },
          body: JSON.stringify({
            walletAddress: auth.walletAddress,
            nonce: auth.nonce,
            user: auth.did.toLowerCase().replaceAll('_uuid', ':uuid').replaceAll('_addr', ':addr').replaceAll('_asa', ':asa').replaceAll('_app', ':app').replaceAll('_txn', ':txn')
          })
        });
      } catch (error) {
        console.error('Error stopping stream:', error);
      } finally {
        if (onStop) {
          onStop({
            id: `stopped-${Date.now()}`,
            content: "Agentic flow aborted!",
            sender: "assistant",
            created_at: new Date().toISOString()
          });
        }
        resetState();
      }
    }
  }, [state.currentTaskId, state.abortController, resetState]);

  const processStreamLine = useCallback((
    line: string,
    onData: (data: StreamData) => void,
    onUsage?: (usage: UsageMetadata) => void
  ) => {
    if (!line.startsWith('data: ')) return;

    try {
      const jsonStr = line.slice(6);
      if (!jsonStr.trim()) return;

      const data = JSON.parse(jsonStr);

      // Handle message content
      if (data.answer && typeof data.answer === 'string') {
        // Parse JSON string if needed
        if (data.answer.startsWith('"') && data.answer.endsWith('"')) {
          try {
            data.answer = JSON.parse(data.answer);
          } catch (e) {
            console.warn('Failed to parse JSON answer, using as-is');
          }
        }
        // Replace escaped characters
        data.answer = data.answer.replace(/\\n/g, '\n')
          .replace(/\\t/g, '\t')
          .replace(/\\"/g, '"')
          .replace(/\\\\/g, '\\');
      }

      // Handle task ID
      if (data.event === 'task_id' || (data.event === 'workflow_started' && data.task_id)) {
        setState(prev => ({ ...prev, currentTaskId: data.task_id }));
      }

      // Handle message end and usage data
      if (data.event === 'message_end') {
        if (data.metadata?.usage && onUsage) {
          onUsage(data.metadata.usage);
        }
        setState(prev => ({ ...prev, sending: false }));
      }

      onData(data);
    } catch (error) {
      console.error('Error processing SSE line:', error);
    }
  }, []);

  const startStream = useCallback(async (context: StreamContext) => {
    const { auth, content, conversationId, onData, tools = [], selectedAgents = [] } = context;
    const isDeepThink = tools.find(t => t.name === "Deep Think")?.active?.toString() === "true";
    const isCoder = tools.find(t => t.name === "Coder")?.active?.toString() === "true";
    const loadingToastId = isDeepThink || isCoder ? notify.loading(`${isDeepThink ? 'Deep Think' : 'Coder'} agent tooling flow takes longer to complete...`) : null;

    setState(prev => ({ ...prev, sending: true }));
    const controller = new AbortController();
    setState(prev => ({ ...prev, abortController: controller }));

    try {
      // Create a comma-separated string of selected agent IDs
      const selectedAgentsString = selectedAgents.map(agent => agent.id).join(',');
      
      let options: any = {
        walletAddress: auth.walletAddress,
        nonce: auth.nonce,
        inputs: {
          // Ensure we're using the current tool state for this request
          "deepthink": tools.find(t => t.name === "Deep Think")?.active?.toString() || 'false',
          // "mcpagent": tools.find(t => t.name === "MCP Mode")?.active?.toString() || 'false',
          // "asalytic": tools.find(t => t.name === "Asalytic")?.active?.toString() || 'false',
          // "vestige": tools.find(t => t.name === "Vestige")?.active?.toString() || 'false',
          // "websearch": tools.find(t => t.name === "Web Search")?.active?.toString() || 'false',
          "coder": tools.find(t => t.name === "Coder")?.active?.toString() || 'false',
          "algorand_elements": 'null',
          "previous_conversations": 'null',
          "user_wallet": auth.walletAddress,
          "user_handle": auth.walletAddress,
          "provider": 'algorand',
          "selected_agents": selectedAgentsString || 'null',
        },
        query: content,
        user: auth.did.toLowerCase().replaceAll('_uuid', ':uuid').replaceAll('_addr', ':addr').replaceAll('_asa', ':asa').replaceAll('_app', ':app').replaceAll('_txn', ':txn'),
        response_mode: 'streaming',
        auto_generate_name: false,

        files: null,
      }
      if (conversationId && conversationId !== null) {
        options = { ...options, conversation_id: conversationId }
      }
      const response = await fetch(`${getAiApiUrl()}/assistant`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.jwt}`
        },
        body: JSON.stringify(options),
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error(await response.text() || 'Failed to send message');
      }

      if (!response.body) {
        throw new Error('No response stream available');
      }

      const reader = response.body.getReader();

      const processChunk = async (usage?: UsageMetadata) => {
        if (usage) {
          try {
            const newCredit = await updateTokenUsage(auth, {
              prompt_tokens: usage.prompt_tokens,
              completion_tokens: usage.completion_tokens,
              total_tokens: usage.total_tokens
            });
            return newCredit;
          } catch (error) {
            console.error('Error updating token usage:', error);
            notify.error('Failed to update credits');
          }
        }
      };


      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        bufferRef.current += decoderRef.current.decode(value, { stream: true });

        if (bufferRef.current.length > MAX_BUFFER_SIZE) {
          console.error('SSE buffer overflow, clearing buffer');
          bufferRef.current = bufferRef.current.slice(-MAX_BUFFER_SIZE / 2);
        }

        const lines = bufferRef.current.split('\n');

        for (let i = 0; i < lines.length - 1; i++) {
          const line = lines[i].trim();
          if (line) {
            processStreamLine(line, onData, async (usage) => {
              await processChunk(usage);
            });
          }
        }

        bufferRef.current = lines[lines.length - 1];
      }

      if (bufferRef.current.trim()) {
        processStreamLine(bufferRef.current.trim(), onData);
      }
      loadingToastId && notify.dismiss(loadingToastId);
      return true;
    } catch (error: any) {
      loadingToastId && notify.dismiss(loadingToastId);
      if (error.name === 'AbortError') {
        notify.info('Response generation stopped');
        return false;
      }
      throw error;
    } finally {
      resetState();
    }
  }, [processStreamLine, resetState]);

  return {
    state,
    stopStream,
    startStream
  };
};
